import openai from './openaiClient';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error info with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Matches clients to properties based on their preferences using OpenAI GPT-5.
 * @param {Object} client - Client object with preferences.
 * @param {Array} properties - Array of available properties.
 * @returns {Promise<Array>} Sorted array of properties with match scores and reasons.
 */
export async function matchClientToProperties(client, properties) {
  try {
    const clientPreferences = `
Client Name: ${client?.name}
Category: ${client?.category}
Preferred Location: ${client?.preferredLocation || 'Any'}
Budget Range: ${client?.budgetMin || 0} - ${client?.budgetMax || 'No limit'} ${properties?.[0]?.currency || 'JOD'}
Property Type: ${client?.propertyType || 'Any'}
Bedrooms: ${client?.bedrooms || 'Any'}
Priority: ${client?.priority}
Notes: ${client?.notes || 'None'}
    `?.trim();

    const propertiesData = properties?.map(p => ({
      id: p?.id,
      title: p?.title,
      type: p?.type,
      purpose: p?.purpose,
      bedrooms: p?.bedrooms,
      bathrooms: p?.bathrooms,
      area: p?.area,
      price: p?.price,
      city: p?.city,
      district: p?.district,
      address: p?.address,
      status: p?.status,
      features: p?.features,
      description: p?.description
    }));

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert real estate matching assistant. Analyze client preferences and property details to provide intelligent matches with detailed reasoning. Consider location, budget, property type, size, features, and client priorities.' 
        },
        { 
          role: 'user', 
          content: `Match this client to the available properties and rank them by suitability.\n\nClient Preferences:\n${clientPreferences}\n\nAvailable Properties:\n${JSON.stringify(propertiesData, null, 2)}\n\nProvide matches with scores (0-100) and detailed reasoning for each match.` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'property_matches',
          schema: {
            type: 'object',
            properties: {
              matches: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    propertyId: { type: 'string' },
                    matchScore: { type: 'number' },
                    reasoning: { type: 'string' },
                    pros: { type: 'array', items: { type: 'string' } },
                    cons: { type: 'array', items: { type: 'string' } },
                    recommendation: { type: 'string' }
                  },
                  required: ['propertyId', 'matchScore', 'reasoning']
                }
              },
              overallAnalysis: { type: 'string' }
            },
            required: ['matches', 'overallAnalysis'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'high'
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    
    // Merge match data with original property data
    const matchedProperties = result?.matches?.map(match => {
      const property = properties?.find(p => p?.id?.toString() === match?.propertyId);
      return {
        ...property,
        matchScore: match?.matchScore,
        matchReasoning: match?.reasoning,
        matchPros: match?.pros || [],
        matchCons: match?.cons || [],
        matchRecommendation: match?.recommendation || ''
      };
    })?.filter(p => p?.id)?.sort((a, b) => b?.matchScore - a?.matchScore);

    return {
      matches: matchedProperties,
      overallAnalysis: result?.overallAnalysis
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error matching client to properties:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Get property recommendations for a client based on preferences.
 * @param {Object} clientPreferences - Client preference object.
 * @param {Array} properties - Array of available properties.
 * @returns {Promise<Object>} Recommendations with insights.
 */
export async function getPropertyRecommendations(clientPreferences, properties) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are a real estate advisor. Provide quick property recommendations based on client needs.' 
        },
        { 
          role: 'user', 
          content: `Client is looking for: ${JSON.stringify(clientPreferences)}. Available properties: ${JSON.stringify(properties?.slice(0, 10))}. Recommend top 3 properties with brief reasons.` 
        },
      ],
      reasoning_effort: 'minimal',
      verbosity: 'low',
      max_completion_tokens: 500
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error getting property recommendations:', error);
    }
    throw new Error(errorInfo.message);
  }
}
